import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, CircularProgress, Dialog, DialogContent, TextField,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LinkIcon from '@mui/icons-material/Link';
import { useDispatch } from 'react-redux';

import { createList, addItemsToList } from '../../redux/features/lists/listsSlice';
import { importItems } from '../../redux/features/items/itemsSlice';
import { setSnackbar } from '../../redux/features/snackbarSlice';
import {
  BORDER, BORDER_HOVER, FOCUS_RING, MUTED, SANS_STACK, SUCCESS, TEXT_DARK, TEXT_SECONDARY,
} from '../common/tokens';
import {
  DIALOG_PAPER_SX, DialogCloseButton as CloseButton, DialogFooter as Footer,
  DialogHeader as Header, DialogPrimaryButton as PrimaryButton,
  DialogTextButton as TextButton, DialogTitle as Title,
} from '../common/dialog/primitives';
import FolderTree from './FolderTree';
import { parseUrls, readBookmarkFile } from './parseBookmarks';

const MAX_CHARS = 10000;
const MAX_URLS = 100;

// Items land asynchronously, so the views are told twice: once now for whatever
// already exists, and again once the server has had time to finish the rest.
const LATE_REFRESH_MS = 6000;

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13.5,
    fontFamily: SANS_STACK,
    color: TEXT_DARK,
    borderRadius: '4px',
    '& fieldset': { borderColor: BORDER, borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: BORDER_HOVER },
    '&.Mui-focused fieldset': { borderColor: SUCCESS, borderWidth: '1.5px' },
    '&.Mui-focused': { boxShadow: FOCUS_RING },
  },
  '& .MuiOutlinedInput-input::placeholder': { color: MUTED, opacity: 1 },
};

const toggleGroupSx = (theme) => ({
  borderRadius: 1,
  overflow: 'hidden',
  border: `1.5px solid ${BORDER}`,
  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: 0,
    px: 2.5,
    py: 0.75,
    gap: 0.75,
    textTransform: 'none',
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: TEXT_SECONDARY,
    '&.Mui-selected': {
      color: '#fff',
      backgroundColor: theme.palette.primary.main,
      '&:hover': { backgroundColor: theme.palette.primary.dark },
    },
  },
});

function announceItemsChanged() {
  window.dispatchEvent(new CustomEvent('itemsChanged'));
  setTimeout(() => window.dispatchEvent(new CustomEvent('itemsChanged')), LATE_REFRESH_MS);
}

// `lead` differs per mode — imported items versus created lists — but what
// didn't land reads the same either way.
function importSummary(lead, { skipped, failed }) {
  return [
    lead,
    skipped > 0 && `${skipped} already existed`,
    failed > 0 && `${failed} failed`,
  ].filter(Boolean).join(', ');
}

function ImportDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const [text, setText] = useState('');
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [manualMode, setManualMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const isFolderMode = folders.length > 0 && !manualMode;
  const urls = parseUrls(text);
  const urlCount = urls.length;
  const charCount = text.length;
  const isOverLimit = charCount > MAX_CHARS || urlCount > MAX_URLS;

  const totalSelectedUrls = folders.reduce((sum, f) => sum + f.selectedUrls.filter(Boolean).length, 0);
  const isFolderOverLimit = totalSelectedUrls > MAX_URLS;

  const canImport = isFolderMode
    ? totalSelectedUrls > 0 && !isFolderOverLimit
    : urlCount > 0 && !isOverLimit;

  const handleClose = () => {
    if (loading) return;
    setText('');
    setFolders([]);
    setManualMode(false);
    onClose();
  };

  const handleFileUpload = (e) => {
    const input = e.target;
    const file = input.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const parsed = readBookmarkFile(evt.target.result);
      if (parsed.folders.length > 0) {
        setFolders(parsed.folders);
        setManualMode(false);
        setText('');
      } else {
        // No folders found — fall back to flat URL extraction
        setText(parsed.urls.join('\n'));
        setFolders([]);
      }
    };
    reader.readAsText(file);
    input.value = '';
  };

  const toggleFolder = (fi, checked) => {
    setFolders((prev) => prev.map((f, i) => (
      i === fi ? { ...f, selectedUrls: f.selectedUrls.map(() => checked) } : f
    )));
  };

  const toggleUrl = (fi, ui, checked) => {
    setFolders((prev) => prev.map((f, i) => {
      if (i !== fi) return f;
      const selectedUrls = f.selectedUrls.map((v, j) => (j === ui ? checked : v));
      return { ...f, selectedUrls };
    }));
  };

  const toggleExpanded = (fi) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(fi)) next.delete(fi);
      else next.add(fi);
      return next;
    });
  };

  const importFolder = async (folder) => {
    const selectedUrls = folder.urls.filter((_, i) => folder.selectedUrls[i]);
    if (selectedUrls.length === 0) return { listCreated: false, created: 0, skipped: 0, failed: 0 };
    try {
      const list = await dispatch(createList({ namespace: 'private', listInfo: { title: folder.name } })).unwrap();
      const items = selectedUrls.map((url) => ({ links: [{ url, primary: true }] }));
      const result = await dispatch(addItemsToList({ namespace: 'private', listId: list.id, items })).unwrap();
      return {
        listCreated: true,
        created: result.created,
        skipped: result.skipped,
        failed: result.failed,
      };
    } catch {
      // List creation or the request itself failed — the whole folder failed.
      return { listCreated: false, created: 0, skipped: 0, failed: selectedUrls.length };
    }
  };

  const handleImport = async () => {
    if (!canImport || loading) return;
    setLoading(true);

    if (isFolderMode) {
      const results = await Promise.all(folders.map(importFolder));
      const listsCreated = results.filter((r) => r.listCreated).length;
      const skipped = results.reduce((sum, r) => sum + r.skipped, 0);
      const failed = results.reduce((sum, r) => sum + r.failed, 0);
      const itemsCreated = results.reduce((sum, r) => sum + r.created + r.skipped, 0);

      setLoading(false);
      setFolders([]);
      setManualMode(false);
      onClose();
      announceItemsChanged();

      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: failed > 0 ? 'warning' : 'success',
        snackbarMessage: importSummary(
          listsCreated > 0 && `${listsCreated} list${listsCreated !== 1 ? 's' : ''} created (${itemsCreated} items)`,
          { skipped, failed },
        ),
      }));
    } else {
      dispatch(importItems({ urls }))
        .unwrap()
        .then((result) => {
          setLoading(false);
          setText('');
          onClose();
          announceItemsChanged();
          dispatch(setSnackbar({
            snackbarOpen: true,
            snackbarType: result.failed > 0 ? 'warning' : 'success',
            snackbarMessage: importSummary(
              result.created > 0 && `${result.created} imported`,
              result,
            ),
          }));
        })
        .catch((err) => {
          setLoading(false);
          dispatch(setSnackbar({
            snackbarOpen: true,
            snackbarType: 'error',
            snackbarMessage: err.message || 'Import failed',
          }));
        });
    }
  };

  const helperText = isOverLimit
    ? `Exceeds limit (${charCount}/${MAX_CHARS} chars, ${urlCount}/${MAX_URLS} URLs)`
    : `${urlCount} URL${urlCount !== 1 ? 's' : ''} detected · ${charCount}/${MAX_CHARS} chars`;

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      fullWidth
      onClose={handleClose}
      PaperProps={{ elevation: 0, sx: DIALOG_PAPER_SX }}
    >
      <Header>
        <Title>Import URLs</Title>
        <CloseButton onClick={handleClose} aria-label="Close" disabled={loading}>
          <CloseIcon />
        </CloseButton>
      </Header>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        {folders.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <ToggleButtonGroup
              exclusive
              disabled={loading}
              onChange={(_, val) => {
                if (val === 'manual' && !manualMode) {
                  setText(folders.flatMap((f) => f.urls.filter((url, i) => f.selectedUrls[i])).join('\n'));
                  setManualMode(true);
                } else if (val === 'folders' && manualMode) {
                  setManualMode(false);
                }
              }}
              size="small"
              sx={toggleGroupSx}
              value={manualMode ? 'manual' : 'folders'}
            >
              <ToggleButton value="folders"><FolderOpenIcon sx={{ fontSize: 16 }} />Folders</ToggleButton>
              <ToggleButton value="manual"><LinkIcon sx={{ fontSize: 16 }} />Manual</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        {isFolderMode ? (
          <FolderTree
            folders={folders}
            expanded={expandedFolders}
            disabled={loading}
            totalSelected={totalSelectedUrls}
            maxUrls={MAX_URLS}
            overLimit={isFolderOverLimit}
            onToggleFolder={toggleFolder}
            onToggleUrl={toggleUrl}
            onToggleExpand={toggleExpanded}
          />
        ) : (
          <TextField
            autoFocus
            disabled={loading}
            error={isOverLimit}
            fullWidth
            helperText={helperText}
            placeholder="Paste URLs, one per line"
            minRows={8}
            maxRows={16}
            multiline
            value={text}
            onChange={(e) => setText(e.target.value)}
            sx={textFieldSx}
          />
        )}
        <input
          accept=".html,.htm"
          ref={fileInputRef}
          style={{ display: 'none' }}
          type="file"
          onChange={handleFileUpload}
        />
      </DialogContent>

      <Footer sx={{ gap: 1 }}>
        <TextButton
          disabled={loading}
          onClick={() => fileInputRef.current.click()}
          size="small"
          sx={{ mr: 'auto' }}
        >
          Upload bookmark file
        </TextButton>
        <TextButton disabled={loading} onClick={handleClose}>
          Cancel
        </TextButton>
        <PrimaryButton
          disableElevation
          disabled={!canImport || loading}
          onClick={handleImport}
          startIcon={loading ? <CircularProgress size={14} thickness={4} sx={{ color: '#fff' }} /> : null}
          variant="contained"
        >
          {loading ? 'Importing…' : 'Import'}
        </PrimaryButton>
      </Footer>
    </Dialog>
  );
}

ImportDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
};

ImportDialog.defaultProps = {
  open: false,
};

export default ImportDialog;
